import React, { useState, useEffect } from 'react';
import '../styles/Login.css';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchSettings } = useSettings();
  const [successMsg, setSuccessMsg] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      const token = params.get('token');
      if (token) {
        setSuccessMsg('Your email has been successfully verified! Logging you in...');
        setLoading(true);
        axios.get(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/user`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(async (response) => {
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(response.data));
          await fetchSettings();
          if (response.data.role === 'super_admin') {
            navigate('/super-admin');
          } else {
            navigate('/');
          }
        }).catch(err => {
          setLoading(false);
          setError('Auto-login failed. Please login manually.');
        });
      } else {
        setSuccessMsg('Your email has been successfully verified! You may now login.');
      }
    } else if (params.get('verified') === 'error') {
      setError('Email verification failed or link has expired. Please try again or contact support.');
    }
  }, [location, navigate, fetchSettings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/login`, {
        email,
        password
      });

      if (response.data.requires_2fa) {
        navigate('/verify-2fa', { state: { email, method: response.data.method } });
        return;
      }

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Fetch latest settings to sync terminology
      await fetchSettings();

      if (response.data.user.role === 'super_admin') {
        navigate('/super-admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      const errMsg = typeof err.response?.data?.message === 'string'
        ? err.response.data.message
        : 'Invalid credentials. Please try again.';

      setError(errMsg);

      if (errMsg.toLowerCase().includes('verify')) {
        setShowResend(true);
      } else {
        setShowResend(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Please enter your email address to resend the link.');
      return;
    }
    setResending(true);
    setSuccessMsg('');
    setError('');
    try {
      const response = await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/resend-verification-email`, { email });
      setSuccessMsg(response.data.message || 'Verification email resent successfully. Please check your inbox.');
      setShowResend(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div id="auth-page">
      {/* Floating Shapes */}
      <div className="shape shape-1"></div>
      <div className="shape shape-2"></div>
      <div className="shape shape-3"></div>

      <div className="auth-card">
        <div className="icon-container">
          <img src="/logo.svg" alt="LMS Logo" className="logo-img" />
        </div>

        <div className="auth-header text-center mb-4">
          <h3>Welcome Back!</h3>
          <p className="text-muted">Login to your LMS Dashboard</p>
        </div>

        {error && (
          <div className="alert alert-danger py-2 small d-flex flex-column align-items-center text-center">
            <span>{error}</span>
            {error.toLowerCase().includes('verify') && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger mt-2 fw-bold"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? 'Resending...' : 'Resend Verification Email'}
              </button>
            )}
          </div>
        )}
        {successMsg && <div className="alert alert-success py-2 small">{successMsg}</div>}

        <form onSubmit={handleLogin}>
          <div className="mb-3 text-start">
            <label className="form-label fw-bold text-muted small ">Email Address</label>
            <div className="input-group">
              <span className="input-group-text"><i className="bi bi-envelope"></i></span>
              <input
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="mb-4 text-start">
            <label className="form-label fw-bold text-muted small ">Password</label>
            <div className="input-group">
              <span className="input-group-text"><i className="bi bi-lock"></i></span>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="input-group-text bg-white"
                onClick={() => setShowPassword(!showPassword)}
                style={{ borderLeft: 'none', cursor: 'pointer' }}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash text-muted' : 'bi-eye text-primary'}`}></i>
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-auth" disabled={loading}>
            {loading ? (
              <span><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Processing...</span>
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className="mt-4 text-center">
          <small className="text-muted d-block mb-2">
            <a href="/forgot-password" className="text-primary fw-bold hover-underline">Forgot Password?</a>
          </small>
          <small className="text-muted d-block mb-3">Don't have an account? <a href="/signup" className="text-primary fw-bold hover-underline">Sign Up</a></small>


        </div>
      </div>
    </div>
  );
};

export default Login;
